import { Queue } from 'utility/queue';

interface RTCInboundRtpVideoStreamStats extends RTCInboundRtpStreamStats {
    kind: 'video';
    bytesReceived: number;
    framesPerSecond: number;
    frameWidth: number;
}

function isRTCInboundRtpStreamStats(x: RTCStats): x is RTCInboundRtpStreamStats {
    return x.type === 'inbound-rtp';
}

function isRTCInboundRtpVideoStreamStats(x: RTCStats): x is RTCInboundRtpVideoStreamStats {
    return isRTCInboundRtpStreamStats(x) && x.kind === 'video';
}

function getLatestFramerate(stats: RTCInboundRtpVideoStreamStats[]) {
    const last = stats.at(-1);
    if (!last) return undefined;

    //statsが更新されていない場合-1を返す
    const currentTimestamp = new Date().getTime();
    if (currentTimestamp - last.timestamp > 50) return undefined;

    return last.framesPerSecond;
}

function getLatestBitrateInKbps(stats: RTCInboundRtpVideoStreamStats[]) {
    const last = stats.at(-1);
    const prev = stats.at(-2);
    if (!last || !prev) return -1;

    //statsが更新されていない場合-1を返す
    const currentTimestamp = new Date().getTime();
    if ((currentTimestamp - last.timestamp) > 50) return -1;

    const bytesInInterval = last.bytesReceived - prev.bytesReceived;
    const intervalInSec = (last.timestamp - prev.timestamp) / 1000;
    const bitrate = bytesInInterval / intervalInSec * 8;
    return Math.floor(bitrate / 1024);
}

function isBitrateRising(bitrates: number[], threshold: number) {
    const latestBitrate = bitrates[bitrates.length - 1];
    const minBitrate = Math.min(...bitrates);
    if (latestBitrate > minBitrate * threshold) {
        return true;
    } else {
        return false;
    }
}

function getStreamByBitrate(bitratesTable: Array<Queue<number>>): [selectedIndex: number, isTentative: boolean] | undefined {
    const xs = bitratesTable
        .map((bitrates, index) => ({
            rising: isBitrateRising(bitrates.array, 5),
            latest: bitrates.at(-1) ?? 0, // FIXME
            prev: bitrates.at(-2) ?? 0, // FIXME
            index
        }));
    const sortedByLatest = xs.sort((a, b) => b.latest - a.latest);

    const firstPromisingStream = xs.find(x => {
        if (!x.rising) return false;

        const best = sortedByLatest.find(y => y.index !== x.index);
        if (best === undefined) return false; // this must not be happened
        if (x.latest > best.latest * 0.75) return true;
        return false;
    });
    if (firstPromisingStream) return [firstPromisingStream.index, false];

    const [topOfLatest, nextOfLatest] = sortedByLatest;
    if (topOfLatest.latest > nextOfLatest.latest * 2) {
        const [topOfPrev, nextOfPrev] = xs.sort((a, b) => b.prev - a.prev);
        if (topOfPrev.prev > nextOfPrev.prev * 2) {
            if (topOfLatest === topOfPrev) return [topOfLatest.index, true];
        }
    }

    return undefined;
}

function sum(arr: number[]) {
    return arr.reduce((prev, current) => prev + current, 0);
}

function avg(arr: number[]) {
    return Math.floor(sum(arr) / arr.length);
}

function getBestStreamIndexByFramerate(values: Queue<number>[], threshHigh: number): number | 'matched' | 'deferred' {
    const averageFrameRates = values.map((value, index) => ({ avg: value.length > 0 ? avg(value.array) : -1, index }));

    const [top, next] = averageFrameRates.sort((a, b) => b.avg - a.avg);
    if (top.avg < 0) return 'deferred';

    if (top.avg > next.avg * threshHigh) return top.index;

    return 'matched';
}

function getFrameWidthByIndex(stats: RTCInboundRtpVideoStreamStats[], index: number) {
    const length = stats.length;
    if (length < index + 1) return -1;

    //statsが更新されていない場合-1を返す
    const currentTimestamp = new Date().getTime();
    if ((currentTimestamp - stats[length - 1].timestamp) > 50) return -1;

    return stats[length - (index + 1)].frameWidth ?? 0;
}

function getBestStreamIndexByResolution(statsTable: Array<Queue<RTCInboundRtpVideoStreamStats>>): number | 'matched' | 'deferred' {
    const lastFrameWidths = statsTable.map((stats, index) => ({ width: getFrameWidthByIndex(stats.array, 0), index }));
    const [topOfLastFrame, nextOfLastFrame] = lastFrameWidths.sort((a, b) => b.width - a.width);
    if (topOfLastFrame.width === nextOfLastFrame.width) {
        return 'matched';
    }

    const prevFrameWidths = statsTable.map((stats, index) => ({ width: getFrameWidthByIndex(stats.array, 1), index }));
    const [topOfPrevFrame, nextOfPrevFrame] = prevFrameWidths.sort((a, b) => b.width - a.width);
    if (topOfPrevFrame.width === nextOfPrevFrame.width) {
        return 'deferred';
    }

    if (topOfLastFrame.index === topOfPrevFrame.index) return topOfLastFrame.index;

    return 'deferred';
}

function getQPByIndex(stats: RTCInboundRtpStreamStats[], index: number) {
    //statsが更新されていない場合-1を返す
    const currentTimestamp = new Date().getTime();
    const last = stats.at(- 1);
    if (!last) return -1;
    if ((currentTimestamp - last.timestamp) > 50) return -1;

    const target = stats.at(-index - 1);
    const prev = stats.at(-index - 1);
    if (!target || !prev) return -1;

    const framesDecoded0 = target.framesDecoded ?? 0;
    const framesDecoded1 = prev.framesDecoded ?? 0;
    const deltaFrames = framesDecoded0 - framesDecoded1;
    if (deltaFrames < 1) return -1; // each framesDecoded must be integer

    const qpSum0 = target.qpSum ?? 0;
    const qpSum1 = prev.qpSum ?? 0;
    return Math.floor((qpSum0 - qpSum1) / deltaFrames);
}

function maxIndex<T>(xs: [T, ...T[]]) {
    let maxIndex = 0;

    for (let i = 1; i < xs.length; ++i) {
        if (xs[maxIndex] < xs[i]) {
            maxIndex = i;
        }
    }

    return maxIndex;
}

function isNonEmpty<T>(xs: T[]): xs is [T, ...T[]] {
    return xs.length > 0;
}

function getBestStreamIndexByQP(statsTable: Queue<RTCInboundRtpVideoStreamStats>[]) {
    const lastQPs = statsTable.map(stats => getQPByIndex(stats.array, 0));
    if (!isNonEmpty(lastQPs) || lastQPs.find(v => v <= 0)) return 'deferred';
    const bestStreamIndexInLastStat = maxIndex(lastQPs);

    const prevQPs = statsTable.map(stats => getQPByIndex(stats.array, 1));
    if (!isNonEmpty(prevQPs) || prevQPs.find(v => v <= 0)) return 'deferred';
    const bestStreamIndexInPrevStat = maxIndex(prevQPs);

    if (bestStreamIndexInLastStat === bestStreamIndexInPrevStat) {
        return bestStreamIndexInPrevStat;
    }

    return 'deferred';
}

export class StreamSelector {
    private statsTable = new Array<Queue<RTCInboundRtpVideoStreamStats>>();
    private bitratesTable = new Array<Queue<number>>();
    private framerateTable = new Array<Queue<number>>();

    private currentStream = 0;
    private avoidUnstableCount = 0;

    get index() { return this.currentStream; }

    push(...streams: (RTCStatsReport | undefined)[]) {
        streams.forEach((stream, index) => {
            const stats = this.statsTable[index] = this.statsTable.at(index) ?? new Queue(100);
            if (stream) {
                stream.forEach((report: RTCStats) => {
                    if (isRTCInboundRtpVideoStreamStats(report)) {
                        stats.push(report);
                    }
                });
            } else {
                stats.clear();
            }
        });
    }

    getTargetStreamIndex(): number {
        // update framerate
        this.statsTable.forEach((stats, index) => {
            const framerate = getLatestFramerate(stats.array);
            if (framerate !== undefined) {
                const frameRates = this.framerateTable[index] = this.framerateTable.at(index) ?? new Queue(10);
                frameRates.push(framerate);
            }
        });

        //check the bitrate

        const latestBitrates = this.statsTable.map((stats) => getLatestBitrateInKbps(stats.array));
        const firstAvailableStreamIndex = Math.max(0, latestBitrates.findIndex(bitrate => bitrate >= 0));
        let availableStreamCount = 0;
        latestBitrates.forEach((bitrate, index) => {
            const bitrates = this.bitratesTable[index] = this.bitratesTable.at(index) ?? new Queue(4);
            if (bitrate >= 0) {
                availableStreamCount++;
                if (index >= firstAvailableStreamIndex) {
                    bitrates.push(bitrate);
                }
            }
        });
        if (availableStreamCount === 1) {
            return firstAvailableStreamIndex;
        }

        if (this.bitratesTable.some(bitrates => bitrates.length < 2)) {
            // we still don't have enough bitrate values
            return this.currentStream;
        }

        if (this.avoidUnstableCount > 0) {
            // avoiding unstable stream change
            this.avoidUnstableCount--;
            return this.currentStream;
        }

        const result = getStreamByBitrate(this.bitratesTable);
        if (result) {
            const [bitrateTargetStream, isTentative] = result;

            if (!isTentative) {
                // this selection may be stable for a coming few seconds
                this.avoidUnstableCount = 5;
            }

            this.currentStream = bitrateTargetStream;
            return this.currentStream;
        }

        // check by video quality

        // resolution
        const resultByResolution = getBestStreamIndexByResolution(this.statsTable);
        if (resultByResolution === 'deferred') return this.currentStream;
        if (resultByResolution !== 'matched') {
            this.currentStream = resultByResolution;
            return this.currentStream;
        }

        // framerate
        const resultByFramerate = getBestStreamIndexByFramerate(this.framerateTable, 1.4);
        if (resultByFramerate === 'deferred') return this.currentStream;
        if (resultByFramerate !== 'matched') {
            this.currentStream = resultByFramerate;
            return this.currentStream;
        }

        // QP
        const resultByQP = getBestStreamIndexByQP(this.statsTable);
        if (resultByQP === 'deferred') return this.currentStream;
        this.currentStream = resultByQP;
        return this.currentStream;
    }
}
